import React, { useMemo, useEffect, useState } from "react";
import Card from "./card";
import {
  Container,
  Row,
  TailRow,
  CardShell,
  CardContainer,
  MetricLabel,
  AvailWrap,
  AvailHeader,
  DonutWrap,
  DonutSvg,
  DonutBlock,
  KindList,
  KindRow,
  Dot,
} from "./styles";
import HypervisorSummarySkeleton from "./hypervisorSummarySkeleton";

type Summary = {
  count: number;
  cpu: { used: number; total: number };
  ram: { used: number; total: number };
  gpu: { used: number; total: number };
  available_gpu: Record<string, number>;
  kinds: Record<string, number>;
};

const pct = (u: number, t: number) => (t > 0 ? Math.round((u / t) * 100) : 0);

const colors = [
  ["#60a5fa", "#3b82f6"],
  ["#34d399", "#10b981"],
  ["#a78bfa", "#8b5cf6"],
  ["#fbbf24", "#f59e0b"],
  ["#fb7185", "#f43f5e"],
  ["#2dd4bf", "#14b8a6"],
  ["#94a3b8", "#64748b"],
];

const HypervisorSummary: React.FC<{ summary: Summary | null }> = ({
  summary,
}) => {
  const cpuP = pct(summary?.cpu?.used ?? 0, summary?.cpu?.total ?? 0);
  const ramP = pct(summary?.ram?.used ?? 0, summary?.ram?.total ?? 0);
  const gpuP = pct(summary?.gpu?.used ?? 0, summary?.gpu?.total ?? 0);

  const gpuEntries = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.available_gpu)
      .map(([key, val]) => {
        const num = parseInt(key.replace(/[^\d]/g, ""), 10);
        return [key, Number(val), isNaN(num) ? 9999 : num] as [
          string,
          number,
          number
        ];
      })
      .sort((a, b) => a[2] - b[2])
      .map(([label, value]) => [label, Number(value)]);
  }, [summary]);

  const totalAvail = gpuEntries.reduce((acc, [, v]) => acc + v, 0);

  const kindEntries = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.kinds)
      .filter(([_, v]) => Number(v) > 0)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .reverse()
      .map(([label, value]) => [label.toUpperCase(), Number(value)]);
  }, [summary]);

  const totalKinds = kindEntries.reduce((acc, [, v]) => acc + v, 0);

  const [availDisplay, setAvailDisplay] = useState(0);
  const [kindDisplay, setKindDisplay] = useState(0);

  useEffect(() => {
    if (!summary) return;

    const animate = (setter: any, target: number) => {
      const start = performance.now();
      const duration = 700;
      const tick = (t: number) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setter(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    animate(setAvailDisplay, totalAvail);
    animate(setKindDisplay, totalKinds);
  }, [summary, totalAvail, totalKinds]);

  if (!summary) {
    return <HypervisorSummarySkeleton />;
  }

  const createDonut = (entries, total, idPrefix) => {
    const r = 48;
    const c = 2 * Math.PI * r;
    let offset = 0;

    return entries.map(([label, val], idx) => {
      const value = Number(val);
      const fraction = total > 0 ? value / total : 0;
      const len = fraction * c * 0.98;

      const gradId = `${idPrefix}-grad-${idx}`;
      const [c1, c2] = colors[idx % colors.length];

      const seg = (
        <React.Fragment key={label}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={c1} />
              <stop offset="100%" stopColor={c2} />
            </linearGradient>
          </defs>
          <circle
            r={r}
            cx="50%"
            cy="50%"
            fill="transparent"
            stroke={`url(#${gradId})`}
            strokeWidth="12"
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-(offset % c)}
          />
        </React.Fragment>
      );

      offset += len + c * 0.005;
      return seg;
    });
  };

  const renderDonut = (
    title: string,
    total: number,
    displayVal: number,
    entries: [string, number][],
    idPrefix: string
  ) => (
    <CardContainer>
      <AvailWrap>
        <AvailHeader>
          <MetricLabel>{title}</MetricLabel>
        </AvailHeader>

        <DonutBlock>
          <DonutWrap>
            <DonutSvg viewBox="0 0 130 130">
              <circle
                r="48"
                cx="50%"
                cy="50%"
                fill="transparent"
                stroke="#f1f5f9"
                strokeWidth="12"
              />
              {createDonut(entries, total, idPrefix)}
            </DonutSvg>

            <div className="center">
              <span className="num">{displayVal}</span>
              <span className="sub">total</span>
            </div>
          </DonutWrap>

          <KindList>
            {entries.map(([label, count], idx) => (
              <KindRow key={label}>
                <Dot $idx={idx} />
                <span className="label">{label}</span>
                <span className="count">{count}</span>
              </KindRow>
            ))}
          </KindList>
        </DonutBlock>
      </AvailWrap>
    </CardContainer>
  );

  return (
    <Container>
      <Row>
        <CardShell>
          <Card
            title="CPU Usage"
            unit="Core"
            percent={cpuP}
            used={summary.cpu.used}
            total={summary.cpu.total}
            accent="#3b82f6"
          />
        </CardShell>

        <CardShell>
          <Card
            title="RAM Usage"
            unit="GB"
            percent={ramP}
            used={summary.ram.used}
            total={summary.ram.total}
            accent="#10b981"
          />
        </CardShell>

        <CardShell>
          <Card
            title="VGPU Usage"
            unit="GPU"
            percent={gpuP}
            used={summary.gpu.used}
            total={summary.gpu.total}
            accent="#8b5cf6"
          />
        </CardShell>
      </Row>

      <TailRow>
        {renderDonut(
          "Available VGPU",
          totalAvail,
          availDisplay,
          gpuEntries,
          "vgpu"
        )}

        {renderDonut(
          "Instance Kinds",
          totalKinds,
          kindDisplay,
          kindEntries,
          "kind"
        )}
      </TailRow>
    </Container>
  );
};

export default HypervisorSummary;
